import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { searchInspectors, useSaveInspector, type InspectorSummary } from '../../../api/inspectors';
import { isApiError } from '../../../api/errors';
import { Autocomplete, Button, ButtonGroup, ErrorSummary, InsetText, Radios, Table, type ErrorSummaryItem } from '../../../components/idsk';
import { applyServerErrors, summaryItems } from '../../../components/common/FormErrors';
import { useAppForm } from '../../../components/common/useAppForm';
import { useAnnouncer } from '../../../layout/Announcer';
import { inspectorSchema, type InspectorFormValues } from '../../../validation/schemas';
import { EMPTY_INSPECTOR, INSPECTOR_SERVER_FIELDS, InspectorFields, inspectorFieldOrder } from '../../inspectors/InspectorFields';
import { toInspectorRequest } from '../../inspectors/InspectorFormPage';
import { useWizard } from './WizardContext';
import { WizardStep } from './WizardStep';
import { MAX_MEMBERS, validateStep2, type StepErrors, type WizardMember } from './validation';

export function Step2Team() {
  const { t } = useTranslation();
  const { state, update } = useWizard();
  const { announce } = useAnnouncer();
  const [errors, setErrors] = useState<StepErrors>({});
  const [submitCount, setSubmitCount] = useState(0);
  const [showNew, setShowNew] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const teamHeadingRef = useRef<HTMLHeadingElement>(null);
  const newSectionId = useId();

  const addMember = (member: WizardMember) => {
    if (state.members.some((m) => m.id === member.id)) {
      announce(t('wizard.step2.alreadyMember', { name: member.displayName }));
      return false;
    }
    if (state.members.length >= MAX_MEMBERS) {
      announce(t('wizard.errors.membersTooMany'));
      return false;
    }
    update({ members: [...state.members, member], leaderId: state.leaderId ?? member.id });
    announce(t('wizard.step2.added', { name: member.displayName }));
    return true;
  };

  const removeMember = (index: number) => {
    const member = state.members[index]!;
    const members = state.members.filter((_, i) => i !== index);
    update({ members, leaderId: state.leaderId === member.id ? null : state.leaderId });
    announce(t('wizard.step2.removed', { name: member.displayName }));
    // focus: the next row's remove button, else the previous one, else the search
    requestAnimationFrame(() => {
      const next = members[index] ?? members[index - 1];
      const target = next ? document.getElementById(`remove-${next.id}`) : searchRef.current;
      target?.focus();
    });
  };

  const onContinue = () => {
    const e = validateStep2(state);
    setErrors(e);
    setSubmitCount((c) => c + 1);
    return Object.keys(e).length === 0;
  };
  const summary: ErrorSummaryItem[] = [
    ...(errors.members ? [{ targetId: 'team-search', text: t(errors.members) }] : []),
    ...(errors.leader ? [{ targetId: 'leader', text: t(errors.leader) }] : []),
  ];

  return (
    <WizardStep
      step={2}
      title={t('wizard.step2.title')}
      errors={summary}
      submitCount={submitCount}
      onContinue={onContinue}
      before={
        <>
          <Autocomplete<InspectorSummary>
            id="team-search"
            inputRef={searchRef}
            label={t('wizard.step2.search')}
            required
            labelClassName="govuk-label--m"
            hint={t('wizard.step2.searchHint')}
            error={errors.members ? t(errors.members) : undefined}
            search={searchInspectors}
            getOptionKey={(i) => i.id}
            getOptionLabel={(i) => i.displayName}
            getOptionHint={(i) => i.email}
            selectionText={() => ''}
            onSelect={(i) => addMember({ id: i.id, displayName: i.displayName, email: i.email })}
          />
          <p className="govuk-body">
            <Button variant="secondary" aria-expanded={showNew} aria-controls={newSectionId} onClick={() => setShowNew((v) => !v)}>
              {t('wizard.step2.addNew')}
            </Button>
          </p>
          <div id={newSectionId} hidden={!showNew}>
            {showNew && (
              <NewInspectorForm
                onCancel={() => setShowNew(false)}
                onSaved={(m) => {
                  addMember(m);
                  setShowNew(false);
                  requestAnimationFrame(() => teamHeadingRef.current?.focus());
                }}
              />
            )}
          </div>
          <h2 className="govuk-heading-m" tabIndex={-1} ref={teamHeadingRef}>
            {t('wizard.step2.members', { count: state.members.length })}
          </h2>
          {state.members.length === 0 ? (
            <InsetText>{t('wizard.step2.noMembers')}</InsetText>
          ) : (
            <Table<WizardMember>
              caption={t('wizard.step2.tableCaption')}
              captionClassName="govuk-visually-hidden"
              rows={state.members}
              rowKey={(m) => m.id}
              columns={[
                { key: 'name', header: t('inspectors.fields.name'), rowHeader: true, render: (m) => m.displayName },
                { key: 'email', header: t('inspectors.fields.email'), render: (m) => m.email },
                {
                  key: 'actions',
                  header: t('common.actions'),
                  render: (m, index) => (
                    <Button id={`remove-${m.id}`} variant="secondary" className="govuk-!-margin-bottom-0" onClick={() => removeMember(index)}>
                      {t('common.delete')} <span className="govuk-visually-hidden">{m.displayName}</span>
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </>
      }
    >
      {state.members.length > 0 && (
        <Radios
          name="leader"
          idPrefix="leader"
          legend={t('wizard.step2.leader')}
          required
          legendClassName="govuk-fieldset__legend--m"
          hint={t('wizard.step2.leaderHint')}
          error={errors.leader ? t(errors.leader) : undefined}
          items={state.members.map((m) => ({ value: m.id, label: m.displayName }))}
          value={state.leaderId}
          onChange={(leaderId) => update({ leaderId })}
        />
      )}
    </WizardStep>
  );
}

/** "Pridať nového inšpektora": all inspector fields with validation; saving stores the inspector and adds them. */
function NewInspectorForm({ onSaved, onCancel }: { onSaved: (m: WizardMember) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const save = useSaveInspector();
  const [other, setOther] = useState<string[]>([]);
  const { register, handleSubmit, formState, setError } = useAppForm<InspectorFormValues>(inspectorSchema, EMPTY_INSPECTOR);
  const errors = summaryItems(t, formState.errors, inspectorFieldOrder('new-inspector'));
  const submit = handleSubmit(async (values) => {
    setOther([]);
    try {
      const saved = await save.mutateAsync(toInspectorRequest(values));
      onSaved({ id: saved.id, displayName: saved.displayName, email: saved.email });
    } catch (e) {
      const unmatched = applyServerErrors(e, setError, INSPECTOR_SERVER_FIELDS);
      if (!isApiError(e) || e.fieldErrors.length === 0 || unmatched.length) setOther([isApiError(e) ? e.message : t('errors.unexpected'), ...unmatched]);
    }
  });
  return (
    <form className="app-repeating-row" onSubmit={(e) => void submit(e)} noValidate aria-labelledby="new-inspector-heading">
      {(errors.length > 0 || other.length > 0) && (
        <ErrorSummary errors={[...errors, ...other.map((text) => ({ text }))]} focusKey={formState.submitCount} />
      )}
      <h2 className="govuk-heading-m" id="new-inspector-heading">
        {t('wizard.step2.newHeading')}
      </h2>
      <InspectorFields prefix="new-inspector" register={register} errors={formState.errors} />
      <ButtonGroup>
        <Button type="submit" preventDoubleClick>
          {t('wizard.step2.saveNew')}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </ButtonGroup>
    </form>
  );
}
